import { useEffect, useState } from "react";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { NumberField } from "../DailyReportForm";
import { btnPrimary, fmtDateTimeWIB, fmtIDR, inp, todayWIB } from "../util";
import { Card, OwnerActions, Pill, Revisions, Row, call, useLoad } from "./common";
import { NotRequired } from "./SalesClosing";
import FinanceEvidence from "./ai/FinanceEvidence";

const cats = (cfg) => ["Ready Stock", ...cfg.po_countries.map((c) => `PO ${c}`)];
const rows = (list, cfg) => {
    const have = Object.fromEntries((list || []).map((r) => [r.category, r]));
    const names = [...cats(cfg), ...Object.keys(have).filter((k) => !cats(cfg).includes(k))];
    return names.map((c) => have[c] || { category: c, amount_idr: 0 });
};

function Session({ title, window, list, onChange, tid, disabled }) {
    const upd = (i, v) => onChange(list.map((r, j) => (j === i ? { ...r, amount_idr: v, ...(r.ongkir_idr > v ? { ongkir_idr: v } : {}) } : r)));
    const tot = list.reduce((a, r) => a + r.amount_idr, 0);
    return (
        <Card title={title} testid={tid} right={<span className="text-[11px] text-neutral-500" data-testid={`${tid}-window`}>{window}</span>}>
            <fieldset disabled={disabled} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {list.map((r, i) => (
                    <NumberField key={r.category} k={`${tid}-${i}-amount`} label={`${r.category} — Cash In`} type="money" value={r.amount_idr} onChange={(_, v) => upd(i, v)} />
                ))}
            </fieldset>
            <Row label="Total Sesi" value={fmtIDR(tot)} bold testid={`${tid}-total`} />
        </Card>
    );
}

export default function ReportFinance({ cfg, focus }) {
    const { user } = useAuth();
    const rd = focus?.date || todayWIB();
    const [f, , setF] = useLoad("/reporting/v2/finance", { date: rd });
    const [d, setD] = useState(null);
    const [notes, setNotes] = useState("");
    const [aiOk, setAiOk] = useState(false);
    useEffect(() => { if (f) { setD({ session1: rows(f.data?.session1, cfg), session2: rows(f.data?.session2, cfg) }); setNotes(f.data?.notes || ""); } }, [f, cfg]);
    if (!f || !d) return <div className="text-sm text-neutral-400">Memuat…</div>;
    if (f.not_required) return <NotRequired rd={rd} testid="finance-not-required" />;
    const isFin = user.role === "finance", locked = f.status === "APPROVED" || !isFin;
    const sum = (s) => d[s].reduce((a, r) => a + r.amount_idr, 0);
    const byCat = cats(cfg).map((c) => [c, ["session1", "session2"].reduce((a, s) => a + (d[s].find((r) => r.category === c)?.amount_idr || 0), 0)]);
    const save = async () => {
        const r = await call(() => api.put("/reporting/v2/finance", { data: { ...d, notes }, submit: true }, { params: { date: rd } }), f.first_submitted_at ? "Koreksi tersimpan" : "Report Finance terkirim");
        if (r) setF(r);
    };
    return (
        <div className="space-y-4 max-w-3xl" data-testid="report-finance">
            <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500"><span className="text-sm text-ink font-medium">Laporan Finance {rd}</span><Pill status={f.status} testid="finance-status" />
                {f.first_submitted_at && <span>Dikirim {fmtDateTimeWIB(f.first_submitted_at)} oleh {f.first_submitted_by}</span>}</div>
            <p className="text-xs text-neutral-500">Isi total uang yang benar-benar diterima di rekening (sudah termasuk ongkir), per kategori dan sesi. Contoh: produk Rp980.000 + ongkir Rp20.000 → Cash In Rp1.000.000.</p>
            {f.windows.carry_forward && <p className="text-xs rounded-sm border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2" data-testid="fin-carry-note">Sesi 1 otomatis mencakup periode hari Tidak Wajib Lapor ({f.windows.carried_days?.join(", ")}): <b>{f.windows.session1}</b>. Cukup kirim satu Report Finance.</p>}
            <Session title="Sesi 1" window={f.windows.session1} list={d.session1} onChange={(v) => setD({ ...d, session1: v })} tid="fin-s1" disabled={locked} />
            <Session title="Sesi 2" window={f.windows.session2} list={d.session2} onChange={(v) => setD({ ...d, session2: v })} tid="fin-s2" disabled={locked} />
            <Card title="Total Cash In" testid="fin-totals">
                {byCat.map(([c, v]) => <Row key={c} label={c} value={fmtIDR(v)} />)}
                <Row label="Total Cash In harian" value={fmtIDR(sum("session1") + sum("session2"))} bold testid="fin-grand-total" />
            </Card>
            {!locked && <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Catatan Finance (opsional)" className={inp} rows={2} data-testid="fin-notes" />}
            {!locked && <FinanceEvidence rd={rd} d={d} onPass={setAiOk} />}
            {!locked && <button type="button" disabled={!aiOk} className={`${btnPrimary} w-full disabled:opacity-40`} onClick={save} data-testid="fin-submit-btn"><PaperPlaneTilt size={14} /> {f.first_submitted_at ? "Simpan koreksi" : "Kirim Report Finance"}</button>}
            {f.reconciliation && <p className="text-[11px] text-neutral-500" data-testid="fin-recon">Rekonsiliasi: Tidak Dapat Dibandingkan — {f.reconciliation.reason}</p>}
            {user.role === "owner" && <div className="space-y-2"><OwnerActions doc={f} base="/reporting/v2/finance" onDone={setF} /><Revisions list={f.revisions} /></div>}
        </div>
    );
}
